import {DataStatus, Label, isCurrency} from 'platform/components';
import {HStack, Heading, Show, Stack, VStack, ThemeIconKey, Icon, Text} from 'platform/foundation';
import {useCurrencySymbolFormatter, useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useShowPaymentPrescriptionQuery} from '@dms/api/finance';
import {OrderPaymentResponseBody} from '@dms/api/sales';
import {Money} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, useGetAccountingDocuments} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {getPaymentInvoiceInfo} from '../../../utils/getPaymentInvoiceInfo';

interface DepositPriceBoxProps extends TestIdProps {
  payment: OrderPaymentResponseBody;
}

/**
 * @deprecated
 * This is kept to satisfy use-case
 * when prescriptionId in checkout is not available
 */
export const PaymentPriceBox = (props: DepositPriceBoxProps) => {
  const formatCurrency = useFormatCurrency();
  const formatCurrencySymbol = useCurrencySymbolFormatter();

  const {id, type} = getPaymentInvoiceInfo(props.payment);

  const {
    data: invoice,
    isError: isErrorInvoice,
    isLoading: isLoadingInvoice,
  } = useGetAccountingDocuments(id, type);

  const {
    data: prescription,
    isLoading: isPrescriptionLoading,
    isError: isPrescriptionError,
  } = useShowPaymentPrescriptionQuery(
    {paymentPrescriptionId: invoice?.paymentPrescriptionId ?? ''},
    {skip: isNilOrEmpty(invoice?.paymentPrescriptionId)}
  );

  const formatSymbol = (val: string | Nullish, showSymbol = true) =>
    match([isCurrency(val), showSymbol])
      .with([true, true], always(`(${isCurrency(val) ? formatCurrencySymbol(val) : val})`))
      .otherwise(always(''));

  const formatPrice = (val: Money | Nullish) =>
    isNotNil(val)
      ? (formatCurrency(parseFloat(val.amount), val.currency, 2) ?? EMPTY_PLACEHOLDER)
      : EMPTY_PLACEHOLDER;

  const isLoading = isLoadingInvoice || isPrescriptionLoading;
  const isError = isErrorInvoice || isPrescriptionError;

  const price = prescription?.price ?? props.payment.foreignCurrencyAmount ?? props.payment.amount;
  const remainsToPay =
    prescription?.remainsToPay ?? props.payment.foreignCurrencyAmount ?? props.payment.amount;

  const directionIcon = match<'incoming' | 'outgoing' | undefined, ThemeIconKey | undefined>(
    prescription?.direction
  )
    .with('incoming', always('navigation/arrow_forward'))
    .with('outgoing', always('navigation/arrow_back'))
    .otherwise(always(undefined));

  return (
    <DataStatus isError={isError} isLoading={isLoading} grow={1} spacing={5}>
      <Stack direction={['column', 'row']} spacing={4}>
        <VStack width={41}>
          <Label>{`${i18n.t('general.labels.amount')} ${formatSymbol(
            price?.currency,
            false
          )}`}</Label>
          <Heading size={4} data-testid={suffixTestId('amount', props)}>
            {formatPrice(price)}
          </Heading>
        </VStack>

        <Show when={props.payment.paymentState !== 'PAID'}>
          <VStack width={41}>
            <Label>{`${i18n.t('entity.invoice.labels.remaining')} ${formatSymbol(
              remainsToPay?.currency,
              false
            )}`}</Label>
            <Heading size={4} color="danger">
              {formatPrice(remainsToPay)}
            </Heading>
          </VStack>

          <Show when={prescription?.direction}>
            <VStack width={41}>
              <Label>{i18n.t('general.labels.direction')}</Label>
              <HStack align="center" spacing={1}>
                <Icon value={directionIcon} size={3} />
                <Text data-testid={suffixTestId('paymentDirection', props)}>
                  {i18n.t(`entity.prescription.labels.${prescription?.direction}`)}
                </Text>
              </HStack>
            </VStack>
          </Show>
        </Show>
      </Stack>
    </DataStatus>
  );
};
